// Grouped, searchable product-line picker for the create-preset-menu modal.
//
// Mirrors the events menu-items picker (EventMenuItemsSection/ProductPickerDropdown):
// a SearchableDropdown (Popover + cmdk Command) with a tab strip (All + one per
// service) above a list whose items are grouped under service section headers.
// The list is static (no network), so cmdk does the text filtering; the tabs
// narrow which service groups are visible. Selection stores the backend key.

import { useMemo, useState } from 'react';

import { CommandEmpty, CommandGroup } from '@/components/ui/command';
import { FieldTrigger } from '@/components/ui/field-trigger';
import { ScrollableTabs } from '@/components/ui/scrollable-tabs';
import { SearchableDropdown } from '@/components/ui/searchable-dropdown';
import { DropdownListItem } from '@/react/components/ui/dropdown-list-item';
import { ChevronDownIcon } from '@/react/components/icons/ChevronDownIcon';
import { cn } from '@/lib/utils';

import { PRODUCT_LINE_GROUPS, PRODUCT_LINE_LABELS } from './productLines';

// Per-service heading styling — targets the cmdk group-heading wrapper, matching
// the events picker: 14px/600 grey text, 12px outdent, bottom hairline divider.
const HEADING_CLASSES = cn(
  '[&_[cmdk-group-heading]]:mx-4',
  '[&_[cmdk-group-heading]]:py-4',
  '[&_[cmdk-group-heading]]:font-inter',
  '[&_[cmdk-group-heading]]:text-sm',
  '[&_[cmdk-group-heading]]:font-semibold',
  '[&_[cmdk-group-heading]]:leading-normal',
  '[&_[cmdk-group-heading]]:text-grey',
  '[&_[cmdk-group-heading]]:border-b',
  '[&_[cmdk-group-heading]]:border-dinnerplate-white',
);

export interface ProductLinePickerProps {
  /** Selected product-line key ('' when none). */
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  /** Label for the leading "All" tab. */
  allLabel: string;
  ariaLabel: string;
  hasError?: boolean;
  /**
   * When provided, restricts the picker to the single group whose `form`
   * matches this value. The tab strip is hidden (no point with one group).
   * When absent/empty, all groups are shown with the full tab strip.
   */
  form?: string;
}

export function ProductLinePicker({
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  allLabel,
  ariaLabel,
  hasError = false,
  form,
}: ProductLinePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(allLabel);

  // When `form` is provided, restrict to that form's group only and hide tabs.
  const formFilteredGroups = useMemo(
    () =>
      form
        ? PRODUCT_LINE_GROUPS.filter((group) => group.form === form)
        : PRODUCT_LINE_GROUPS,
    [form],
  );

  const tabs = useMemo(
    () => [allLabel, ...formFilteredGroups.map((group) => group.service)],
    [allLabel, formFilteredGroups],
  );
  const visibleGroups = useMemo(
    () =>
      form
        ? formFilteredGroups
        : activeTab === allLabel
          ? PRODUCT_LINE_GROUPS
          : PRODUCT_LINE_GROUPS.filter((group) => group.service === activeTab),
    [form, formFilteredGroups, activeTab, allLabel],
  );

  const selectedLabel = value ? PRODUCT_LINE_LABELS[value] ?? value : '';

  return (
    <SearchableDropdown
      isOpen={isOpen}
      onOpenChange={(next) => {
        setIsOpen(next);
        if (!next) setActiveTab(allLabel);
      }}
      shouldFilter
      searchPlaceholder={searchPlaceholder}
      selectedValue={value || undefined}
      // Always open downward (below the field), never flipping up over the modal
      // header. The list height below is capped so the whole dropdown still fits
      // beneath the trigger with bottom padding.
      side="bottom"
      avoidCollisions={false}
    >
      <SearchableDropdown.Trigger>
        <FieldTrigger aria-label={ariaLabel} className={cn(hasError && 'border-chilli-red-light bg-chilli-blush')}>
          <span className={cn('truncate', !value && 'text-grey-light')}>
            {selectedLabel || placeholder}
          </span>
          <ChevronDownIcon className="shrink-0 text-grey" />
        </FieldTrigger>
      </SearchableDropdown.Trigger>

      {!form && (
        <SearchableDropdown.Tabs>
          <ScrollableTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        </SearchableDropdown.Tabs>
      )}

      {/* Use the SearchableDropdown.List default height (320px), matching the
          events menu-items picker (ProductPickerDropdown), rather than a shorter cap. */}
      <SearchableDropdown.List showFadeGradient>
        <CommandEmpty className="px-4 py-3 text-left text-sm text-grey">{emptyText}</CommandEmpty>
        {visibleGroups.map((group, index) => (
          <CommandGroup
            key={group.service}
            // Form-filtered to a single group → the heading is redundant noise; drop it
            // (and its styling) so it reads as a plain searchable list.
            heading={form ? undefined : group.service}
            className={cn('overflow-visible p-0', !form && HEADING_CLASSES, !form && index > 0 && 'mt-4')}
          >
            <div className="flex flex-col">
              {group.options.map((option) => (
                <DropdownListItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label]}
                  onSelect={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  isSelected={option.value === value}
                  primary={option.label}
                  primaryTitle={option.label}
                />
              ))}
            </div>
          </CommandGroup>
        ))}
      </SearchableDropdown.List>
    </SearchableDropdown>
  );
}
